import { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import authService from '../../utils/authService';
import config from '../../config';
import { formatDateToString } from '../../utils/dateUtils';

// Import shared components
import DateRangePicker from '../shared/DateRangePicker';
import GuestSelector from '../shared/GuestSelector';
import CitySearch from '../shared/CitySearch';
import HotelFilters from '../shared/HotelFilters';
import HotelResults from '../shared/HotelResults';
import LoadingOverlay from '../shared/LoadingOverlay';

const AgentSearchHotel = () => {
  const outletContext = useOutletContext();
  const setHeaderAgentDetails = outletContext?.setHeaderAgentDetails;

  const updateAgentDetailsLocally = (details) => {
    setAgentDetails(details);
    if (setHeaderAgentDetails) {
      setHeaderAgentDetails(details);
    }
  };

  const [loading, setLoading] = useState(true);
  const [searchHistory, setSearchHistory] = useState([]);
  const [agentDetails, setAgentDetails] = useState(null);
  
  // Unified search state - no more dual state management
  const [searchId, setSearchId] = useState('');
  const [hotelResults, setHotelResults] = useState({});
  const [totalHotels, setTotalHotels] = useState(0);
  const [hasMoreHotels, setHasMoreHotels] = useState(false);
  const [apiStatus, setApiStatus] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchProgress, setSearchProgress] = useState(0);
  
  // Search form state
  const [searchForm, setSearchForm] = useState({
    destination: '',
    state: '',
    country: '',
    checkInDate: '',
    checkOutDate: '',
    selectedDateRange: '',
    rooms: [{
      roomNumber: 1,
      adults: 2,
      children: 0,
      childAges: []
    }],
    minValue: '1000',
    maxValue: '200000',
    previousSearch: false
  });

  // Filter states
  const [filters, setFilters] = useState({
    hotelName: '',
    minPrice: 1000,
    maxPrice: 200000,
    twoStars: false,
    threeStars: false,
    fourStars: false,
    fiveStars: false,
    onlyMatched: false
  });

  const [filterLoading, setFilterLoading] = useState(false);

  // Filters ref to access latest filters in SSE callbacks
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Helper to check if any filters are currently active
  const hasActiveFilters = (f) => {
    const active = f || filtersRef.current || filters;
    return Boolean(
      (active.hotelName && active.hotelName.trim() !== '') ||
      active.twoStars ||
      active.threeStars ||
      active.fourStars ||
      active.fiveStars ||
      active.onlyMatched
    );
  };

  // SSE and timer refs - FIXED
  const sseRef = useRef(null);
  const loadingTimerRef = useRef(null);

  // Initialize component
  useEffect(() => {
    initializeComponent();
    
    return () => {
      cleanup();
    };
  }, []);

  // Initialize component with proper session validation
  const initializeComponent = async () => {
    try {
      setLoading(true);
      
      // First, validate authentication (without cleanup)
      await validateAuthentication();
      
      // Then fetch page data with session restoration
      const hasSessionDates = await fetchPageDataWithSessionRestore();
      
      // Initialize default date range if no session data
      if (!hasSessionDates) {
        initializeDateRange();
      }
      
    } catch (error) {
      console.error('❌ Error initializing component:', error);
      toast.error('Failed to initialize search page');
    } finally {
      setLoading(false);
    }
  };

  // Validate authentication and preserve session data
  const validateAuthentication = async () => {
    try {
      // Validate current user profile with session preservation flag
      const profileResponse = await fetch(`${config.API_BASE_URL}/api/auth/profile?preserveSession=true`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (profileResponse.status === 401) {
        authService.logout();
        window.location.href = '/login';
        return;
      }

      const profileData = await profileResponse.json();
      if (!profileData.success || !profileData.sessionValid) {
        authService.logout();
        window.location.href = '/login';
        return;
      }
    } catch (error) {
      console.error('❌ Error validating authentication:', error);
      authService.logout();
      window.location.href = '/login';
    }
  };

  // Initialize default date range
  const initializeDateRange = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const checkIn = formatDateToString(today);
    const checkOut = formatDateToString(tomorrow);
    
    setSearchForm(prev => ({
      ...prev,
      checkInDate: checkIn,
      checkOutDate: checkOut,
      selectedDateRange: `${formatDate(today)} - ${formatDate(tomorrow)}`
    }));
  };

  // Format date for display
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: '2-digit', 
      year: 'numeric' 
    });
  };

  // Fetch initial page data
  const fetchPageData = async () => {
    try {
      console.log('📡 Fetching agent search hotel data');
      
      const response = await fetch(`${config.API_BASE_URL}/api/agent/dashboard/searchhotel`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        return;
      }

      const data = await response.json();
      
      if (data.success) {
        console.log('📋 Received page data:', data);
        
        // Handle search history
        if (data.searchHistory && Array.isArray(data.searchHistory)) {
          setSearchHistory(data.searchHistory);
          console.log('📜 Search history loaded:', data.searchHistory.length, 'items');
        } else {
          setSearchHistory([]);
          console.log('📜 No search history available');
        }
        
        // Handle agent details (balance and tokens)
        if (data.agentDetails) {
          console.log('💰 Raw agent details from backend:', data.agentDetails);
          
          // Map the backend field names to frontend expected names
          const mappedAgentDetails = {
            balanceAmount: data.agentDetails.walletBalance || '0.00',
            searchTokens: data.agentDetails.tokens || 0,
            agentId: data.agentDetails.agentId,
            name: data.agentDetails.name
          };
          
          console.log('💰 Mapped agent details for AgentSearchHotel:', mappedAgentDetails);
          updateAgentDetailsLocally(mappedAgentDetails);
        }
        
        // Handle existing search data from session
        if (data.searchData) {
          console.log('📋 Found existing search data in session:', data.searchData);
          
          // Update search form with session data
          setSearchForm(prev => ({
            ...prev,
            destination: data.searchData.destination || '',
            state: data.searchData.state || '',
            country: data.searchData.country || '',
            checkInDate: data.searchData.startDate || prev.checkInDate,
            checkOutDate: data.searchData.endDate || prev.checkOutDate,
            selectedDateRange: data.searchData.selectedDateRange || prev.selectedDateRange,
            rooms: data.searchData.rooms || prev.rooms,
            minValue: data.searchData.minValue || prev.minValue,
            maxValue: data.searchData.maxValue || prev.maxValue,
            previousSearch: data.searchData.previousSearch || false
          }));
          
          // Set search ID if available
          if (data.searchData.searchId) {
            setSearchId(data.searchData.searchId);
            console.log('🔍 Search ID set:', data.searchData.searchId);
          }
        }
        
        // Handle existing search results
        if (data.searchResults && Object.keys(data.searchResults).length > 0) {
          console.log('🏨 Found existing search results:', Object.keys(data.searchResults).length, 'provider groups');
          
          setHotelResults(data.searchResults);
          setTotalHotels(data.totalHotels || 0);
          setHasMoreHotels(data.hasMoreHotels || false);
          setCurrentOffset(data.currentOffset || 0);
          setApiStatus(data.apiStatus || false);
          
          // Update filter price range
          if (data.minPrice !== undefined && data.maxPrice !== undefined) {
            setFilters(prev => ({
              ...prev,
              minPrice: data.minPrice,
              maxPrice: data.maxPrice
            }));
            console.log('💰 Price range updated:', data.minPrice, '-', data.maxPrice);
          }
        } else {
          console.log('🏨 No existing search results found');
          // Reset results state
          setHotelResults({});
          setTotalHotels(0);
          setHasMoreHotels(false);
          setCurrentOffset(0);
          setApiStatus(false);
        }
        
        // Handle any error messages
        if (data.invalidError) {
          toast.error(data.invalidError);
        }
        
        // Check if session was cleared
        if (data.sessionCleared) {
          console.log('🧹 Session was cleared - showing fresh page');
        }
        
        console.log('✅ Agent search hotel data loaded successfully');
      } else {
        console.error('❌ API returned error:', data.message);
        toast.error(data.message || 'Failed to load search hotel page');
      }
    } catch (error) {
      console.error('❌ Agent search hotel fetch error:', error);
      toast.error('Failed to load search hotel page');
    }
  };

  // Fetch initial page data with session restoration
  const fetchPageDataWithSessionRestore = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/agent/dashboard/searchhotel?restoreSession=true`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        return false;
      }

      const data = await response.json();
      
      if (data.success) {
        // Handle search history
        if (data.searchHistory && Array.isArray(data.searchHistory)) {
          setSearchHistory(data.searchHistory);
        } else {
          setSearchHistory([]);
        }
        
        // Handle agent details (balance and tokens)
        if (data.agentDetails) {
          const mappedAgentDetails = {
            balanceAmount: data.agentDetails.walletBalance || '0.00',
            searchTokens: data.agentDetails.tokens || 0,
            agentId: data.agentDetails.agentId,
            name: data.agentDetails.name
          };
          
          updateAgentDetailsLocally(mappedAgentDetails);
        }
        
        // Handle existing search data from session
        let hasSessionDates = false;
        if (data.searchData) {
          if (data.searchData.startDate && data.searchData.endDate) {
            hasSessionDates = true;
          }
          // Update search form with session data
          setSearchForm(prev => ({
            ...prev,
            destination: data.searchData.destination || '',
            state: data.searchData.state || '',
            country: data.searchData.country || '',
            checkInDate: data.searchData.startDate || prev.checkInDate,
            checkOutDate: data.searchData.endDate || prev.checkOutDate,
            selectedDateRange: data.searchData.selectedDateRange || prev.selectedDateRange,
            rooms: data.searchData.rooms || prev.rooms,
            minValue: data.searchData.minValue || prev.minValue,
            maxValue: data.searchData.maxValue || prev.maxValue,
            previousSearch: data.searchData.previousSearch || false
          }));
          
          // Set search ID if available
          if (data.searchData.searchId) {
            setSearchId(data.searchData.searchId);
          }
        }
        
        // Handle existing search results
        if (data.searchResults && Object.keys(data.searchResults).length > 0) {
          setHotelResults(data.searchResults);
          setTotalHotels(data.totalHotels || 0);
          setHasMoreHotels(data.hasMoreHotels || false);
          setCurrentOffset(data.currentOffset || 0);
          setApiStatus(data.apiStatus || false);
          
          // Update filter price range
          if (data.minPrice !== undefined && data.maxPrice !== undefined) {
            setFilters(prev => ({
              ...prev,
              minPrice: data.minPrice,
              maxPrice: data.maxPrice
            }));
          }
        } else {
          // Reset results state
          setHotelResults({});
          setTotalHotels(0);
          setHasMoreHotels(false);
          setCurrentOffset(0);
          setApiStatus(false);
        }
        
        // Handle any error messages
        if (data.invalidError) {
          toast.error(data.invalidError);
        }
        return hasSessionDates;
      } else {
        toast.error(data.message || 'Failed to load search hotel page');
        return false;
      }
    } catch (error) {
      console.error('❌ Agent search hotel fetch error:', error);
      toast.error('Failed to load search hotel page');
      return false;
    }
  };

  // Handle search form submission
  const handleSearch = async (e) => {
    e.preventDefault();
    
    // Validate form
    console.log('🔍 Search validation - checking form data:', {
      destination: searchForm.destination,
      state: searchForm.state,
      country: searchForm.country,
      checkInDate: searchForm.checkInDate,
      checkOutDate: searchForm.checkOutDate
    });
    
    // For validation, we need destination and country. State can be empty for city-states like Dubai
    if (!searchForm.destination || !searchForm.country) {
      console.log('❌ Validation failed - missing city or country data');
      toast.error('Please select a city from the suggestions');
      return;
    }
    
    if (!searchForm.checkInDate || !searchForm.checkOutDate) {
      console.log('❌ Validation failed - missing dates');
      toast.error('Please select check-in and check-out dates');
      return;
    }

    try {
      setSearchLoading(true);
      setSearchProgress(0);
      setHotelResults({});
      setHasMoreHotels(false);
      setCurrentOffset(0);
      setTotalHotels(0);
      
      console.log('🔍 Starting hotel search...');

      // Prepare search request
      const searchRequest = {
        destination: searchForm.destination,
        state: searchForm.state,
        country: searchForm.country,
        latitude: searchForm.latitude,
        longitude: searchForm.longitude,
        startDate: searchForm.checkInDate,
        endDate: searchForm.checkOutDate,
        selectedDateRange: searchForm.selectedDateRange,
        rooms: searchForm.rooms,
        minValue: searchForm.minValue,
        maxValue: searchForm.maxValue,
        previousSearch: searchForm.previousSearch
      };

      const response = await fetch(`${config.API_BASE_URL}/api/search/hotels`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include',
        body: JSON.stringify(searchRequest)
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        return;
      }

      const result = await response.json();
      
      if (result.success) {
        if (result.hasError) {
          toast.error(result.errorMessage || 'Search completed with errors');
          setSearchLoading(false);
          return;
        }
        
        // Update search history
        if (result.searchHistory) {
          setSearchHistory(result.searchHistory);
        }
        
        // Update agent details
        if (result.agentDetails) {
          console.log('💰 Raw agent details from search result:', result.agentDetails);
          
          // Map the backend field names to frontend expected names
          const mappedAgentDetails = {
            balanceAmount: result.agentDetails.walletBalance || '0.00',
            searchTokens: result.agentDetails.tokens || 0,
            agentId: result.agentDetails.agentId,
            name: result.agentDetails.name
          };
          
          console.log('💰 Mapped agent details from search result:', mappedAgentDetails);
          updateAgentDetailsLocally(mappedAgentDetails);
        }
        
        // Update price range from search result
        if (result.minPrice !== undefined && result.maxPrice !== undefined) {
          console.log('💰 Updating price range from search result:', result.minPrice, '-', result.maxPrice);
          setFilters(prev => ({
            ...prev,
            minPrice: result.minPrice,
            maxPrice: result.maxPrice
          }));
          
          // Also update the search form min/max values
          setSearchForm(prev => ({
            ...prev,
            minValue: result.minPrice != null ? result.minPrice.toString() : '1000',
            maxValue: result.maxPrice != null ? result.maxPrice.toString() : '200000'
          }));
        }
        
        const newSearchId = result.searchData?.searchId;
        if (newSearchId) {
          setSearchId(newSearchId);
          console.log('🔍 New search ID:', newSearchId);
          
          // Start SSE stream for real-time updates
          startSSEStream(newSearchId);
        } else {
          toast.error('No search ID received');
          setSearchLoading(false);
        }
      } else {
        toast.error(result.message || 'Search failed. Please try again.');
        setSearchLoading(false);
      }
    } catch (error) {
      console.error('❌ Search error:', error);
      toast.error('Search failed. Please try again.');
      setSearchLoading(false);
    }
  };

  // Start SSE stream for real-time hotel updates
  const startSSEStream = (searchIdParam) => {
    console.log('🔄 Starting SSE stream for searchId:', searchIdParam);
    
    if (sseRef.current) {
      sseRef.current.close();
    }

    startProgressAnimation();

    const eventSource = new EventSource(
      `${config.API_BASE_URL}/api/search/stream?searchId=${encodeURIComponent(searchIdParam)}`
    );
    
    sseRef.current = eventSource;

    const timeoutId = setTimeout(() => {
      eventSource.close();
      console.log('SSE timeout reached (3 minutes)');
      setSearchLoading(false);
      setSearchProgress(100);
    }, 3 * 60 * 1000);

    eventSource.addEventListener('fragment-ready', () => {
      console.log('📦 Fragment ready event received');
      if (hasActiveFilters(filtersRef.current)) {
        applyFilters(filtersRef.current, 0, 60);
      } else {
        loadHotelResults(searchIdParam, 0, 60);
      }
    });

    eventSource.addEventListener('loaderTimeout', () => {
      console.log('⏰ Loader timeout event received');
      setSearchProgress(90);
    });

    eventSource.addEventListener('no-hotels', () => {
      console.log('❌ No hotels event received');
      setSearchLoading(false);
      setSearchProgress(100);
      toast.warning('No hotels found for your search criteria');
      eventSource.close();
      clearTimeout(timeoutId);
    });

    eventSource.addEventListener('status', (event) => {
      console.log('✅ Status event received:', event.data);
      if (event.data === 'complete' || event.data === 'stopped') {
        setSearchLoading(false);
        setSearchProgress(100);
        setApiStatus(true);
        eventSource.close();
        clearTimeout(timeoutId);
      }
    });

    eventSource.onerror = (error) => {
      console.error('SSE error:', error);
      setSearchLoading(false);
      eventSource.close();
      clearTimeout(timeoutId);
    };
  };

  // Start progress animation
  const startProgressAnimation = () => {
    const duration = 90000; // 90 seconds
    const startTime = Date.now();
    
    const updateProgress = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min((elapsed / duration) * 100, 90);
      setSearchProgress(progress);
      
      if (progress < 90 && searchLoading) {
        loadingTimerRef.current = setTimeout(updateProgress, 1000);
      }
    };
    
    updateProgress();
  };

  // Load hotel results
  const loadHotelResults = async (searchIdParam, offset = 0, limit = 60) => {
    try {
      const activeFilters = filtersRef.current || filters;
      if (hasActiveFilters(activeFilters)) {
        console.log('🔄 Active filters detected, routing loadHotelResults to applyFilters:', activeFilters);
        await applyFilters(activeFilters, offset, limit);
        return;
      }

      console.log(`📊 Loading hotel results: offset=${offset}, limit=${limit}`);
      
      const response = await fetch(
        `${config.API_BASE_URL}/api/search/hotels?searchId=${encodeURIComponent(searchIdParam)}&offset=${offset}&limit=${limit}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...authService.getAuthHeader()
          },
          credentials: 'include'
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        if (data.success) {
          // Double check filters didn't become active while request was in-flight
          if (hasActiveFilters(filtersRef.current)) {
            console.log('⚠️ Filters changed during hotel load, skipping unfiltered result application');
            return;
          }

          if (offset === 0) {
            setHotelResults(data.searchResults || {});
          } else {
            setHotelResults(prev => {
              const newResults = { ...prev };
              Object.keys(data.searchResults || {}).forEach(key => {
                if (newResults[key]) {
                  const existingList = newResults[key];
                  const incomingList = data.searchResults[key] || [];
                  const mergedMap = new Map();
                  [...existingList, ...incomingList].forEach(item => {
                    const uniqueKey = `${(item.providerName || '').toLowerCase()}_${item.rooms?.totalFare || ''}_${item.hotelId || item.hotelCode || item.hotelName || ''}`;
                    if (!mergedMap.has(uniqueKey)) {
                      mergedMap.set(uniqueKey, item);
                    }
                  });
                  newResults[key] = Array.from(mergedMap.values());
                } else {
                  newResults[key] = data.searchResults[key];
                }
              });
              return newResults;
            });
          }
          
          setHasMoreHotels(data.hasMoreHotels || false);
          setCurrentOffset(data.currentOffset || 0);
          setTotalHotels(data.totalHotels || 0);
          setApiStatus(data.apiStatus || false);
          
          // Update price range from hotel results (only on first load, not on load more)
          if (offset === 0 && data.minPrice !== undefined && data.maxPrice !== undefined) {
            console.log('💰 Updating price range from hotel results:', data.minPrice, '-', data.maxPrice);
            setFilters(prev => ({
              ...prev,
              minPrice: data.minPrice,
              maxPrice: data.maxPrice
            }));
            
            // Also update the search form min/max values
            setSearchForm(prev => ({
              ...prev,
              minValue: data.minPrice != null ? data.minPrice.toString() : '1000',
              maxValue: data.maxPrice != null ? data.maxPrice.toString() : '200000'
            }));
          }
          
          console.log('✅ Hotel results loaded successfully');
        }
      }
    } catch (error) {
      console.error('❌ Error loading hotel results:', error);
    }
  };

  // Handle city selection
  const handleCitySelect = (city) => {
    console.log('🏙️ City received in AgentSearchHotel:', {
      cityName: city.cityName,
      stateName: city.stateName,
      countryName: city.countryName,
      latitude: city.latitude,
      longitude: city.longitude,
      fullObject: city
    });
    
    setSearchForm(prev => ({
      ...prev,
      destination: city.cityName,
      state: city.stateName || '', // Handle null/undefined states
      country: city.countryName,
      latitude: city.latitude !== undefined ? city.latitude : null,
      longitude: city.longitude !== undefined ? city.longitude : null
    }));
    
    console.log('🏙️ Updated search form with city data:', {
      destination: city.cityName,
      state: city.stateName || '',
      country: city.countryName,
      latitude: city.latitude,
      longitude: city.longitude
    });
  };

  // Handle date change
  const handleDateChange = (field, value) => {
    setSearchForm(prev => {
      const newForm = { ...prev, [field]: value };
      
      if (newForm.checkInDate && newForm.checkOutDate) {
        const checkIn = new Date(newForm.checkInDate);
        const checkOut = new Date(newForm.checkOutDate);
        newForm.selectedDateRange = `${formatDate(checkIn)} - ${formatDate(checkOut)}`;
      }
      
      return newForm;
    });
  };

  // Handle rooms change
  const handleRoomsChange = (newRooms) => {
    setSearchForm(prev => ({ ...prev, rooms: newRooms }));
  };

  // Handle filter changes
  const handleFilterChange = async (filterName, value) => {
    const updatedFilters = { ...filters, [filterName]: value };
    filtersRef.current = updatedFilters;
    setFilters(updatedFilters);
    await applyFilters(updatedFilters, 0, 60);
  };

  // Apply filters with pagination support
  const applyFilters = async (filtersToUse, offset = 0, limit = 60) => {
    if (!searchId) {
      console.log('❌ No search ID available for filtering');
      return;
    }

    try {
      setFilterLoading(true);
      console.log('🔍 Applying filters:', filtersToUse, `offset=${offset}, limit=${limit}`);
      console.log('🔍 Current searchId:', searchId);
      
      const params = new URLSearchParams({
        searchId: searchId,
        offset: offset.toString(),
        limit: limit.toString(),
        hotelName: filtersToUse.hotelName || '',
        minValue: (filtersToUse.minPrice != null ? filtersToUse.minPrice : 1000).toString(),
        maxValue: (filtersToUse.maxPrice != null ? filtersToUse.maxPrice : 200000).toString(),
        twoStars: Boolean(filtersToUse.twoStars).toString(),
        threeStars: Boolean(filtersToUse.threeStars).toString(),
        fourStars: Boolean(filtersToUse.fourStars).toString(),
        fiveStars: Boolean(filtersToUse.fiveStars).toString(),
        onlyMatched: Boolean(filtersToUse.onlyMatched).toString()
      });

      console.log('🔍 Filter API URL:', `${config.API_BASE_URL}/api/search/filter-hotels?${params.toString()}`);

      const response = await fetch(
        `${config.API_BASE_URL}/api/search/filter-hotels?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...authService.getAuthHeader()
          },
          credentials: 'include'
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        console.log('🔍 Filter API response:', data);
        
        if (data.success) {
          console.log('✅ Filters applied successfully, updating results');
          let incomingResults = data.searchResults || {};

          // Enforce strict multi-vendor filter on client side as an extra safeguard
          if (filtersToUse.onlyMatched) {
            const matchedOnly = {};
            Object.entries(incomingResults).forEach(([hotelName, options]) => {
              if (Array.isArray(options)) {
                const uniqueProviders = new Set(options.map(o => (o.providerName || '').trim().toLowerCase()).filter(Boolean));
                if (uniqueProviders.size >= 2 || options.length >= 2) {
                  matchedOnly[hotelName] = options;
                }
              }
            });
            incomingResults = matchedOnly;
          }

          if (offset === 0) {
            setHotelResults(incomingResults);
          } else {
            setHotelResults(prev => {
              const newResults = { ...prev };
              Object.keys(incomingResults).forEach(key => {
                if (newResults[key]) {
                  const existingList = newResults[key];
                  const incomingList = incomingResults[key] || [];
                  const mergedMap = new Map();
                  [...existingList, ...incomingList].forEach(item => {
                    const uniqueKey = `${(item.providerName || '').toLowerCase()}_${item.rooms?.totalFare || ''}_${item.hotelId || item.hotelCode || item.hotelName || ''}`;
                    if (!mergedMap.has(uniqueKey)) {
                      mergedMap.set(uniqueKey, item);
                    }
                  });
                  newResults[key] = Array.from(mergedMap.values());
                } else {
                  newResults[key] = incomingResults[key];
                }
              });
              return newResults;
            });
          }
          
          setHasMoreHotels(data.hasMoreHotels || false);
          setCurrentOffset(data.currentOffset != null ? data.currentOffset : offset);
          setTotalHotels(data.totalHotels || Object.keys(incomingResults).length);
          
          // If no results found, log for debugging
          if (!incomingResults || Object.keys(incomingResults).length === 0) {
            console.log('⚠️ No hotels found after applying filters');
            console.log('⚠️ Filter criteria:', filtersToUse);
          }
        } else {
          console.error('❌ Filter API returned error:', data.message);
          toast.error('Failed to apply filters: ' + (data.message || 'Unknown error'));
        }
      } else {
        console.error('❌ Filter API request failed:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
        toast.error('Failed to apply filters');
      }
    } catch (error) {
      console.error('❌ Error applying filters:', error);
      toast.error('Failed to apply filters');
    } finally {
      setFilterLoading(false);
    }
  };

  // Handle load more hotels
  const handleLoadMore = async () => {
    if (!hasMoreHotels || !searchId || filterLoading) {
      console.log('❌ Cannot load more hotels:', { hasMoreHotels, searchId, filterLoading });
      return;
    }
    
    const nextOffset = currentOffset + 60;
    const currentFilters = filtersRef.current || filters;
    console.log('📊 Loading more hotels:', { currentOffset, nextOffset, searchId });
    
    if (hasActiveFilters(currentFilters)) {
      await applyFilters(currentFilters, nextOffset, 60);
    } else {
      await loadHotelResults(searchId, nextOffset, 60);
    }
  };

  // Clear filters
  const handleClearFilters = async () => {
    console.log('🧹 Clearing all filters and reloading original results');
    
    // Reload original search results without any filters
    if (searchId) {
      try {
        console.log('🔄 Reloading original search results for searchId:', searchId);
        
        const response = await fetch(
          `${config.API_BASE_URL}/api/search/hotels?searchId=${encodeURIComponent(searchId)}&offset=0&limit=60`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              ...authService.getAuthHeader()
            },
            credentials: 'include'
          }
        );

        if (response.ok) {
          const data = await response.json();
          
          if (data.success && data.searchResults) {
            console.log('✅ Original search results reloaded successfully');
            
            setHotelResults(data.searchResults || {});
            setHasMoreHotels(data.hasMoreHotels || false);
            setCurrentOffset(0);
            setTotalHotels(data.totalHotels || 0);

            const clearedFilters = {
              hotelName: '',
              minPrice: data.minPrice !== undefined ? data.minPrice : filters.minPrice,
              maxPrice: data.maxPrice !== undefined ? data.maxPrice : filters.maxPrice,
              twoStars: false,
              threeStars: false,
              fourStars: false,
              fiveStars: false,
              onlyMatched: false
            };
            setFilters(clearedFilters);
          }
        }
      } catch (error) {
        console.error('❌ Error reloading original results:', error);
        toast.error('Failed to reload original results');
      }
    } else {
      setFilters({
        hotelName: '',
        minPrice: filters.minPrice,
        maxPrice: filters.maxPrice,
        twoStars: false,
        threeStars: false,
        fourStars: false,
        fiveStars: false,
        onlyMatched: false
      });
    }
  };

  // Handle previous search selection - FIXED VERSION
  const selectPreviousSearch = async (searchItem) => {
    console.log('📋 Selecting previous search:', searchItem);
    
    // Update search form with previous search data
    const updatedForm = {
      destination: searchItem.destination || searchItem.city || '',
      state: searchItem.state || '', // Add state for validation
      country: searchItem.country || '', // Add country for validation
      checkInDate: searchItem.checkIn || searchItem.checkInDate || '',
      checkOutDate: searchItem.checkOut || searchItem.checkOutDate || '',
      rooms: searchItem.rooms && searchItem.rooms.length > 0 ? searchItem.rooms : [{
        roomNumber: 1,
        adults: searchItem.guests || 2,
        children: 0,
        childAges: []
      }],
      minValue: searchItem.minValue || '1000',
      maxValue: searchItem.maxValue || '200000',
      previousSearch: true
    };
    
    // Update date range display
    if (searchItem.checkIn && searchItem.checkOut) {
      const checkIn = new Date(searchItem.checkIn || searchItem.checkInDate);
      const checkOut = new Date(searchItem.checkOut || searchItem.checkOutDate);
      updatedForm.selectedDateRange = `${formatDate(checkIn)} - ${formatDate(checkOut)}`;
    }
    
    // Update the search form state
    setSearchForm(prev => ({
      ...prev,
      ...updatedForm
    }));
    
    console.log('📋 Updated form with previous search data:', updatedForm);
    
    // Wait a moment for state to update, then trigger search automatically
    setTimeout(async () => {
      console.log('🔍 Auto-triggering search for previous search selection');
      
      // Validate the form data
      if (!updatedForm.destination || !updatedForm.country) {
        console.log('❌ Cannot auto-search - missing destination or country');
        toast.error('Please complete the search details and click search');
        return;
      }
      
      if (!updatedForm.checkInDate || !updatedForm.checkOutDate) {
        console.log('❌ Cannot auto-search - missing dates');
        toast.error('Please select check-in and check-out dates');
        return;
      }
      
      // Trigger the search automatically
      try {
        setSearchLoading(true);
        setSearchProgress(0);
        setHotelResults({});
        setHasMoreHotels(false);
        setCurrentOffset(0);
        setTotalHotels(0);
        
        console.log('🔍 Starting automatic hotel search for previous search...');

        // Prepare search request
        const searchRequest = {
          destination: updatedForm.destination,
          state: updatedForm.state,
          country: updatedForm.country,
          startDate: updatedForm.checkInDate,
          endDate: updatedForm.checkOutDate,
          selectedDateRange: updatedForm.selectedDateRange,
          rooms: updatedForm.rooms,
          minValue: updatedForm.minValue,
          maxValue: updatedForm.maxValue,
          previousSearch: updatedForm.previousSearch
        };

        const response = await fetch(`${config.API_BASE_URL}/api/search/hotels`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authService.getAuthHeader()
          },
          credentials: 'include',
          body: JSON.stringify(searchRequest)
        });

        if (response.status === 401) {
          authService.logout();
          window.location.href = '/login';
          return;
        }

        const result = await response.json();
        
        if (result.success) {
          if (result.hasError) {
            toast.error(result.errorMessage || 'Search completed with errors');
            setSearchLoading(false);
            return;
          }
          
          // Update search history
          if (result.searchHistory) {
            setSearchHistory(result.searchHistory);
          }
          
          // Update agent details
          if (result.agentDetails) {
            console.log('💰 Raw agent details from auto-search result:', result.agentDetails);
            
            const mappedAgentDetails = {
              balanceAmount: result.agentDetails.walletBalance || '0.00',
              searchTokens: result.agentDetails.tokens || 0,
              agentId: result.agentDetails.agentId,
              name: result.agentDetails.name
            };
            
            console.log('💰 Mapped agent details from auto-search result:', mappedAgentDetails);
            updateAgentDetailsLocally(mappedAgentDetails);
          }
          
          // Update price range from search result
          if (result.minPrice !== undefined && result.maxPrice !== undefined) {
            console.log('💰 Updating price range from auto-search result:', result.minPrice, '-', result.maxPrice);
            setFilters(prev => ({
              ...prev,
              minPrice: result.minPrice,
              maxPrice: result.maxPrice
            }));
            
            // Also update the search form min/max values
            setSearchForm(prev => ({
              ...prev,
              minValue: result.minPrice != null ? result.minPrice.toString() : '1000',
              maxValue: result.maxPrice != null ? result.maxPrice.toString() : '200000'
            }));
          }
          
          const newSearchId = result.searchData?.searchId;
          if (newSearchId) {
            setSearchId(newSearchId);
            console.log('🔍 New auto-search ID:', newSearchId);
            
            // Start SSE stream for real-time updates
            startSSEStream(newSearchId);
          } else {
            toast.error('No search ID received');
            setSearchLoading(false);
          }
        } else {
          toast.error(result.message || 'Auto-search failed. Please try again.');
          setSearchLoading(false);
        }
      } catch (error) {
        console.error('❌ Auto-search error:', error);
        toast.error('Auto-search failed. Please try again.');
        setSearchLoading(false);
      }
    }, 500); // Wait 500ms for state to update
  };

  // Cleanup function
  const cleanup = () => {
    if (sseRef.current) {
      sseRef.current.close();
    }
    if (loadingTimerRef.current) {
      clearTimeout(loadingTimerRef.current);
    }
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Loading Overlay */}
      {(loading || searchLoading) && (
        <LoadingOverlay 
          loading={loading || searchLoading} 
          progress={searchProgress}
          message={loading ? "Loading search page..." : "Searching hotels..."}
        />
      )}

      <div className="container mx-auto px-6 py-6 max-w-7xl">
        {/* Professional Header */}
        <div className="mb-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Find Your Perfect Stay</h1>
            <p className="text-gray-600 text-sm">Search and compare hotels from multiple providers</p>
          </div>
        </div>

        {/* Modern Search Section */}
        <div className="mb-8">
          {/* Main Search Card */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
              <h2 className="text-white font-semibold text-lg flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Search Hotels
              </h2>
            </div>
            
            <div className="p-6">
              {/* Search Form Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-4">
                {/* Destination */}
                <div className="lg:col-span-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Destination
                  </label>
                  <CitySearch
                    value={searchForm.destination}
                    onCitySelect={handleCitySelect}
                    placeholder="Where are you going?"
                  />
                </div>

                {/* Dates */}
                <div className="lg:col-span-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Check-in & Check-out
                  </label>
                  <DateRangePicker
                    checkInDate={searchForm.checkInDate}
                    checkOutDate={searchForm.checkOutDate}
                    onDateChange={handleDateChange}
                  />
                </div>

                {/* Guests & Rooms */}
                <div className="lg:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Guests & Rooms
                  </label>
                  <GuestSelector
                    rooms={searchForm.rooms}
                    onRoomsChange={handleRoomsChange}
                  />
                </div>

                {/* Search Button */}
                <div className="lg:col-span-1 flex items-end">
                  <button
                    onClick={handleSearch}
                    disabled={searchLoading}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-3 px-4 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    <div className="flex items-center justify-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <span className="text-sm">Search</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Searches - Modern Horizontal Cards */}
          {searchHistory.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 mt-4 overflow-hidden">
              <div className="bg-gray-50 px-6 py-3 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-700 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Recent Searches
                  </h3>
                  <span className="text-xs text-gray-500">Click to reuse</span>
                </div>
              </div>
              
              <div className="p-4">
                <div className="flex space-x-3 overflow-x-auto pb-2" style={{scrollbarWidth: 'thin'}}>
                  {searchHistory.slice(0, 6).map((history, index) => {
                    const destination = history.destination || history.city || 'Unknown City';
                    const checkIn = history.checkIn || history.checkInDate || history.startDate || 'N/A';
                    const checkOut = history.checkOut || history.checkOutDate || history.endDate || 'N/A';
                    
                    // Calculate room-wise information for compact format
                    let roomDetails = [];
                    let totalAdults = 0;
                    let totalChildren = 0;
                    let totalRooms = 0;
                    
                    console.log(`🔍 AgentSearchHotel - Processing search ${index} for ${destination}:`, {
                      rooms: history.rooms,
                      hasRooms: history.rooms && Array.isArray(history.rooms),
                      roomsLength: history.rooms?.length
                    });

                    if (history.rooms && Array.isArray(history.rooms) && history.rooms.length > 0) {
                      // Use rooms array format
                      totalRooms = history.rooms.length;
                      history.rooms.forEach((room, roomIndex) => {
                        const adults = room.adults || room.adultCount || 0;
                        const children = room.children || room.childCount || 0;
                        totalAdults += adults;
                        totalChildren += children;
                        roomDetails.push({
                          roomNumber: roomIndex + 1,
                          adults,
                          children,
                          total: adults + children
                        });
                      });
                    } else {
                      // No rooms data available - use defaults
                      totalAdults = 2;
                      totalChildren = 0;
                      totalRooms = 1;
                      roomDetails.push({
                        roomNumber: 1,
                        adults: 2,
                        children: 0,
                        total: 2
                      });
                    }
                    
                    // Generate compact format: 2R-(2A2C)-(2A2C)
                    let compactFormat = `${totalRooms}R`;
                    if (roomDetails.length > 0) {
                      const roomStrings = roomDetails.map(room => 
                        `(${room.adults}A${room.children > 0 ? `${room.children}C` : ''})`
                      );
                      compactFormat += `-${roomStrings.join('-')}`;
                    }
                    
                    console.log(`🎯 AgentSearchHotel - Compact format for ${destination}: ${compactFormat}`);
                    
                    // Get today's date in YYYY-MM-DD format for comparison
                    const today = formatDateToString(new Date());
                    
                    // Check if dates are in the past
                    const isDateInPast = (dateString) => {
                      if (!dateString || dateString === 'N/A') return false;
                      return dateString < today;
                    };
                    
                    // Check if this search should be disabled (dates in the past)
                    const isDisabled = isDateInPast(checkIn) || isDateInPast(checkOut);
                    
                    const handleClick = () => {
                      if (!isDisabled) {
                        selectPreviousSearch(history);
                      }
                    };
                    
                    return (
                      <div
                        key={index}
                        onClick={handleClick}
                        className={`flex-shrink-0 w-48 rounded-lg p-3 transition-all duration-200 ${
                          isDisabled
                            ? 'bg-gray-100 border border-gray-300 cursor-not-allowed opacity-60'
                            : 'bg-gradient-to-br from-gray-50 to-gray-100 hover:from-blue-50 hover:to-indigo-50 border border-gray-200 hover:border-blue-300 cursor-pointer hover:shadow-md'
                        }`}
                        title={isDisabled ? 'This search has past dates and cannot be used' : 'Click to reuse this search'}
                      >
                        <div className="space-y-2">
                          <div className={`font-medium text-sm truncate flex items-center ${isDisabled ? 'text-gray-500' : 'text-gray-800'}`}>
                            <svg className={`w-3 h-3 mr-1 ${isDisabled ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            </svg>
                            {destination}
                          </div>
                          <div className={`text-xs flex items-center ${isDisabled ? 'text-gray-500' : 'text-gray-600'}`}>
                            <svg className={`w-3 h-3 mr-1 ${isDisabled ? 'text-gray-400' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {checkIn.split('-').slice(1).join('/')} - {checkOut.split('-').slice(1).join('/')}
                          </div>
                          <div className={`text-xs flex items-center ${isDisabled ? 'text-gray-500' : 'text-gray-600'}`}>
                            <svg className={`w-3 h-3 mr-1 ${isDisabled ? 'text-gray-400' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            {compactFormat}
                            {!history.rooms && <span className="ml-1 text-orange-500 italic">(Default)</span>}
                          </div>
                          {isDisabled && (
                            <div className="text-xs text-red-500 font-medium flex items-center">
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                              </svg>
                              Past dates
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Content Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Filters */}
          <div className="lg:col-span-1">
            {searchId && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden sticky top-6">
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-800 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    Refine Results
                  </h3>
                </div>
                <div className="p-4">
                  <HotelFilters
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onClearFilters={handleClearFilters}
                    searchId={searchId}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Main Content - Hotel Results */}
          <div className="lg:col-span-3">
            {Object.keys(hotelResults).length > 0 ? (
              <div className="space-y-6">
                {/* Results Header */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        {totalHotels} Hotels Found
                      </h2>
                      <p className="text-sm text-gray-600 mt-1">
                        Comparing prices from multiple providers
                      </p>
                    </div>
                    <div className="flex items-center space-x-3">
                      {apiStatus && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                          <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                          Live Results
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Hotel Results */}
                <HotelResults
                  hotelResults={hotelResults}
                  totalHotels={totalHotels}
                  hasMoreHotels={hasMoreHotels}
                  onLoadMore={handleLoadMore}
                  searchLoading={searchLoading}
                  apiStatus={apiStatus}
                  searchId={searchId}
                  searchForm={searchForm}
                  onClearFilters={handleClearFilters}
                  filters={filters}
                />
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
                <div className="max-w-md mx-auto">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    {searchId ? (
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    ) : (
                      <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    )}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {searchId ? 'No Hotels Found' : 'Start Your Search'}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {searchId ? 
                      'No hotels match your current criteria. Try adjusting your filters or search parameters.' :
                      'Enter your destination and travel dates to discover amazing hotels.'
                    }
                  </p>
                  {!searchId && (
                    <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                      <div className="flex items-center justify-center p-3 bg-green-50 rounded-lg border border-green-100">
                        <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Best Prices
                      </div>
                      <div className="flex items-center justify-center p-3 bg-blue-50 rounded-lg border border-blue-100">
                        <svg className="w-4 h-4 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Instant Booking
                      </div>
                      <div className="flex items-center justify-center p-3 bg-purple-50 rounded-lg border border-purple-100">
                        <svg className="w-4 h-4 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        24/7 Support
                      </div>
                      <div className="flex items-center justify-center p-3 bg-orange-50 rounded-lg border border-orange-100">
                        <svg className="w-4 h-4 text-orange-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        Secure Payment
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentSearchHotel;